<script setup>
import { computed, ref } from 'vue'
import { RouterLink } from 'vue-router'
import { LayoutDashboard, Upload, History, Settings, BarChart3 } from 'lucide-vue-next'
import QuotaBar from '@/components/billing/QuotaBar.vue'
import UpgradePrompt from '@/components/billing/UpgradePrompt.vue'
import { useAuthStore } from '@/stores/authStore'

const upgradeOpen = ref(false)
const auth = useAuthStore()

import { FRONTEND_VERSION } from '@/version'

// Stamped by the ops CLI on every deployment (empty in local dev).
const buildId = import.meta.env.VITE_BUILD_ID || ''

// Internal accuracy dashboard — link shown only to allowlisted admins. The
// server gates the data (404 otherwise); this just hides the link from everyone else.
const ADMIN_EMAILS = ['tngoldman@gmail.com']
const isAdmin = computed(() => ADMIN_EMAILS.includes((auth.user?.email || '').trim().toLowerCase()))

const navItems = computed(() => [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/upload', label: 'Upload', icon: Upload },
  { to: '/history', label: 'History', icon: History },
  ...(isAdmin.value ? [{ to: '/admin/accuracy', label: 'Accuracy', icon: BarChart3 }] : []),
  // Settings sits at the bottom of the nav, under Accuracy.
  { to: '/settings', label: 'Settings', icon: Settings },
])
</script>

<template>
  <aside class="hidden w-60 shrink-0 border-r border-slate-200 bg-white/60 lg:flex lg:flex-col dark:border-slate-800 dark:bg-slate-950/60">
    <nav class="flex-1 space-y-1 p-4">
      <RouterLink
        v-for="item in navItems"
        :key="item.to"
        :to="item.to"
        class="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
        active-class="bg-primary/10 !text-primary"
      >
        <component :is="item.icon" class="h-5 w-5" />
        {{ item.label }}
      </RouterLink>
    </nav>

    <div class="border-t border-slate-200 p-4 dark:border-slate-800">
      <QuotaBar variant="compact" @upgrade="upgradeOpen = true" />
      <p class="mt-3 text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-600" :title="buildId ? `Deployed build ${buildId}` : 'Local build'">
        v{{ FRONTEND_VERSION }}<template v-if="buildId"> · {{ buildId }}</template>
      </p>
    </div>

    <UpgradePrompt v-model="upgradeOpen" />
  </aside>
</template>
